/**
 * Parent dashboard API service
 * Handles HTTP requests to the parent dashboard and parent sessions endpoints.
 *
 * Implements US-006 (T2): getParentDashboard service function
 * Modified  US-010 (T5): getParentSessions service function added — calls the EXISTING
 *   GET /parent/sessions endpoint (TS-017). Modelled directly on getParentDashboard.
 *   Errors propagate to React Query; never caught here (CCM-03 fail-safe, TO-LL-036).
 * Modified  TS-013 (T2): getTopupCoins service function added — calls the NEW
 *   GET /parent/topup-coins endpoint (API-003, TS-007). Modelled directly on
 *   getParentSessions. Errors propagate (not caught) — TO-LL-036.
 *
 * Requires a parent-group Cognito idToken — group membership is enforced server-side
 * by the TS-017 require_group guard. The frontend ParentGuard provides an additional
 * client-side UX layer preventing students from calling this at all.
 *
 * Errors are never swallowed here — they propagate to the React Query layer so that
 * isError state surfaces correctly to the UI (CCM-03 fail-safe, TO-LL-036).
 */

import { api } from '@/lib/api';
import type { ParentDashboardResponse, ParentSessionsResponse, TopupCoinsResponse } from '@/types/coins';

/**
 * Fetch the authenticated parent's dashboard data including their two-bucket
 * Otter Coin balance (monthlyCoins / topupCoins) and family engagement summary.
 *
 * Uses the shared axios instance from lib/api.ts which auto-attaches
 * Authorization: Bearer {idToken} via the request interceptor.
 *
 * Errors are propagated (not caught) so React Query exposes isError for the
 * ErrorState UI — never returns a fallback on failure.
 *
 * @returns Promise resolving to ParentDashboardResponse
 */
export async function getParentDashboard(): Promise<ParentDashboardResponse> {
  const response = await api.get<ParentDashboardResponse>('/parent/dashboard');
  return response.data;
}

/**
 * Fetch all English-comprehension sessions for the authenticated parent's children,
 * most-recent first, each including starRating, firstTryCorrect/Total, passageTitle,
 * childDisplayName, and (US-010) strengths/improvements from the session report.
 *
 * Implements US-010 (T5): getParentSessions service function.
 * Calls the EXISTING GET /parent/sessions endpoint (TS-017 — already deployed).
 * Modelled directly on getParentDashboard — same axios instance, same error-propagation
 * contract. Errors are NOT caught here; they surface to useParentSessions's React Query
 * error state and the Reports tab renders ErrorState with a retry button (TO-LL-036).
 *
 * TO-LL-036: errors propagated, never swallowed.
 * TO-LL-044: response shape matches ParentSessionsResponse (exact camelCase keys).
 *
 * @returns Promise resolving to ParentSessionsResponse
 */
export async function getParentSessions(): Promise<ParentSessionsResponse> {
  const response = await api.get<ParentSessionsResponse>('/parent/sessions');
  return response.data;
}

/**
 * Fetch the authenticated parent's NON-expired top-up coin batches (API-003, TS-007),
 * soonest-expiry-first, plus totalRemaining. Parent-only server-side.
 *
 * Implements TS-013 (T2): getTopupCoins service function.
 * Modelled directly on getParentSessions/getParentDashboard — same shared axios instance
 * (auto-attaches Authorization: Bearer {idToken}), same error-propagation contract.
 *
 * TO-LL-036: errors propagate (not caught) so React Query surfaces isError.
 * TO-LL-044: response shape matches TopupCoinsResponse (exact camelCase keys).
 *
 * @returns Promise resolving to TopupCoinsResponse
 */
export async function getTopupCoins(): Promise<TopupCoinsResponse> {
  const response = await api.get<TopupCoinsResponse>('/parent/topup-coins');
  return response.data;
}

/**
 * Persist that the authenticated user has seen the first-login welcome banner.
 * Calls POST /users/me/welcome-seen, which sets hasSeenWelcome=true on the user
 * record so the banner stays dismissed across all of the user's devices.
 *
 * Fire-and-forget from the caller's perspective: the banner is a low-stakes UX
 * nicety, so callers swallow failures (worst case it shows once more). Idempotent
 * server-side. Requires an authenticated Cognito idToken (auto-attached by lib/api).
 */
export async function markWelcomeSeen(): Promise<void> {
  await api.post('/users/me/welcome-seen');
}
